/**
 * ENG-8022 (part of ENG-7990 / ADR-0050) - context-carrying restart notice
 * producer (host-side).
 *
 * When a host reboots for patching, the per-agent Slack channel MCP posts a bare
 * "I was restarted mid-conversation..." notice (ENG-5865) to any thread whose
 * inbound was stranded by the restart. That notice is far more useful if it
 * references WHAT the thread was about, so the end user does not have to
 * re-explain.
 *
 * The transcript-reconstruction helpers live here in the manager (apps/cli), but
 * the notice is composed and sent by the channel MCP (packages/mcp), which
 * cannot import from apps/cli (that would be a dependency cycle). So the manager
 * acts as a PRODUCER: at boot, for each agent that has stranded pending-inbound
 * markers on disk, it reconstructs that thread's last topic from the agent's OWN
 * local session transcripts and writes a small per-marker hint file into a
 * sibling dir the channel MCP reads when composing the notice.
 *
 * DATA MINIMISATION: the transcript never leaves the host - same boundary as
 * conversation-eval and memory-extraction. The only thing written is a short
 * topic snippet, into a file on the same host that the same host's MCP reads.
 * And because the stranded notice is posted back into the SAME thread the
 * conversation happened in, referencing that thread's own topic discloses
 * nothing new (everyone who can see the notice can already see the thread).
 *
 * Best-effort and non-throwing: any failure just means the MCP falls back to the
 * bare notice (graceful degradation, ENG-8022 AC#3).
 */

import { readdirSync, readFileSync, writeFileSync, mkdirSync, unlinkSync } from 'node:fs';
import { dirname, join } from 'node:path';

import {
  type Turn,
  reconstructConversation,
  readRecentTurns,
} from './conversation-evaluator.js';
import { sessionTranscriptDir } from './daily-session.js';
import { getProjectDir } from './persistent-session.js';

/** Sibling dir names under ~/.augmented/<codeName>/ (mirrors the MCP consts). */
export const SLACK_PENDING_INBOUND_DIRNAME = 'slack-pending-inbound';
export const SLACK_RESTART_CONTEXT_DIRNAME = 'slack-restart-context';

/** Longest topic snippet embedded in the notice (matches the MCP consumer). */
export const MAX_TOPIC_CHARS = 140;
/** Anything shorter than this after cleaning is not worth referencing. */
const MIN_TOPIC_CHARS = 3;
/** How far back to look for the thread's turns (matches the 7d transcript window). */
const RECONSTRUCT_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
/** Pad the forward edge so a slightly skewed transcript timestamp still lands in. */
const WINDOW_PAD_MS = 5 * 60_000;
/** Cap markers processed per agent so a pathological dir can't stall boot. */
const DEFAULT_MAX_MARKERS_PER_AGENT = 25;

// ADR-0049 (ENG-7891): resolve the per-agent host dir through the getProjectDir
// seam rather than re-joining the augmented root with a codeName, so this stays
// on the right side of the codename->agent_id migration. getProjectDir points at
// <agent-dir>/project; its parent is the agent dir.
function augmentedAgentDir(codeName: string): string {
  return dirname(getProjectDir(codeName));
}
export function slackPendingInboundDir(codeName: string): string {
  return join(augmentedAgentDir(codeName), SLACK_PENDING_INBOUND_DIRNAME);
}
export function slackRestartContextDir(codeName: string): string {
  return join(augmentedAgentDir(codeName), SLACK_RESTART_CONTEXT_DIRNAME);
}

/**
 * The per-marker hint the channel MCP reads. `channel`/`thread_ts` are echoed
 * so the consumer can defensively confirm the hint belongs to the marker it is
 * enriching (a stale hint from a prior boot is ignored, not mis-attributed).
 */
export interface RestartContextHint {
  topic: string;
  channel: string;
  thread_ts: string;
  written_at: string;
}

/** Minimal shape of a stranded pending-inbound marker we need to reconstruct. */
interface StrandedMarkerRef {
  filename: string;
  channel: string;
  thread_ts: string;
}

/**
 * Collapse a raw user message into a single-line, bounded, mention-safe topic
 * snippet. Slack control sequences are neutralised (angle brackets stripped) so
 * re-embedding the snippet in an outbound message can't re-trigger @-mentions,
 * channel broadcasts (<!here>/<!channel>), or resolve links. Returns null when
 * there is nothing usable left.
 */
export function sanitizeTopic(raw: string): string | null {
  const cleaned = raw
    .replace(/\s+/g, ' ')
    .trim()
    // Strip the angle brackets that make Slack mrkdwn sequences special.
    .replace(/[<>]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (cleaned.length < MIN_TOPIC_CHARS) return null;
  if (cleaned.length <= MAX_TOPIC_CHARS) return cleaned;
  return `${cleaned.slice(0, MAX_TOPIC_CHARS - 1).trimEnd()}…`;
}

/**
 * Derive the topic to reference from a reconstructed thread's turns: the most
 * recent USER turn (what the human last asked - exactly what they'd otherwise
 * have to re-explain). `reconstructConversation` returns turns in chronological
 * order with the inbound `<channel>` tag already stripped, so the last user turn
 * is the freshest ask. Returns null when there's no usable user turn.
 */
export function deriveRestartTopic(turns: Turn[]): string | null {
  let lastUserText: string | null = null;
  for (const turn of turns) {
    if (turn.role === 'user' && turn.text.trim()) lastUserText = turn.text;
  }
  if (lastUserText == null) return null;
  return sanitizeTopic(lastUserText);
}

/**
 * Pure core: for each stranded marker, reconstruct its thread from the agent's
 * recent transcript turns and compute the hint to write (or skip it when no
 * topic can be derived). Deterministic given `nowMs`; the fs IO is done by
 * {@link refreshSlackRestartContextHints}. `reconstruct` is injectable for tests.
 */
export function computeRestartContextHints(
  markers: StrandedMarkerRef[],
  allTurns: Turn[],
  nowMs: number,
  reconstruct: typeof reconstructConversation = reconstructConversation,
): Array<{ filename: string; hint: RestartContextHint }> {
  const windowStart = nowMs - RECONSTRUCT_WINDOW_MS;
  const windowEnd = nowMs + WINDOW_PAD_MS;
  const writtenAt = new Date(nowMs).toISOString();
  const out: Array<{ filename: string; hint: RestartContextHint }> = [];
  for (const marker of markers) {
    // A Slack thread's channel_ref is `slack:<channel>:<thread_ts>` - the exact
    // discriminator tokens the inbound `<channel>` tag carries, so this pulls
    // just this thread's turns (channelRefTokens drops the leading `slack`).
    const channelRef = `slack:${marker.channel}:${marker.thread_ts}`;
    const turns = reconstruct(allTurns, channelRef, windowStart, windowEnd);
    const topic = deriveRestartTopic(turns);
    if (!topic) continue;
    out.push({
      filename: marker.filename,
      hint: { topic, channel: marker.channel, thread_ts: marker.thread_ts, written_at: writtenAt },
    });
  }
  return out;
}

function safeReaddir(dir: string): string[] {
  try {
    return readdirSync(dir);
  } catch {
    return [];
  }
}

function readStrandedMarker(path: string): { channel: string; thread_ts: string } | null {
  try {
    const parsed = JSON.parse(readFileSync(path, 'utf-8')) as {
      channel?: unknown;
      thread_ts?: unknown;
    };
    if (typeof parsed.channel === 'string' && typeof parsed.thread_ts === 'string') {
      return { channel: parsed.channel, thread_ts: parsed.thread_ts };
    }
  } catch {
    /* unparseable marker - skip */
  }
  return null;
}

function writeHintFile(path: string, dir: string, hint: RestartContextHint): void {
  mkdirSync(dir, { recursive: true, mode: 0o700 });
  writeFileSync(path, JSON.stringify(hint), { mode: 0o600 });
}

/**
 * Remove every hint file NOT freshly (re)written this run. Keeping only the
 * fresh set clears three stale classes in one pass: (1) orphans whose marker is
 * gone, (2) a LIVE marker that produced no fresh hint this boot - transcripts
 * unavailable/expired, an unparseable marker, or a marker beyond the per-agent
 * cap - and (3) prior-boot leftovers. Since a hint's filename is deterministic
 * from the marker coords, a surviving stale hint would pass the consumer's
 * channel/thread validation and post OUTDATED context instead of the intended
 * bare fallback (CodeRabbit, AC#3). The MCP also unlinks a hint when it consumes
 * it; this is the belt-and-braces, including the replay-ON no-op case.
 */
function pruneHintsExcept(codeName: string, freshFilenames: Set<string>): void {
  const ctxDir = slackRestartContextDir(codeName);
  for (const filename of safeReaddir(ctxDir)) {
    if (!filename.endsWith('.json')) continue;
    if (freshFilenames.has(filename)) continue;
    try {
      unlinkSync(join(ctxDir, filename));
    } catch {
      /* ignore */
    }
  }
}

/**
 * Refresh the restart-context topic hints for a set of agents. Called at manager
 * boot (see startManager). For each agent with stranded pending-inbound markers,
 * read its recent local transcripts once and write a per-marker topic hint the
 * channel MCP reads when composing the "I was restarted" notice. Non-throwing:
 * every fault degrades to the bare notice.
 */
export function refreshSlackRestartContextHints(
  codeNames: string[],
  opts: {
    now?: number;
    log?: (msg: string) => void;
    maxMarkersPerAgent?: number;
    /** Test seam: override transcript reading. */
    readTurns?: (transcriptDir: string, nowMs: number) => Turn[];
  } = {},
): void {
  const nowMs = opts.now ?? Date.now();
  const log = opts.log ?? (() => {});
  const cap = opts.maxMarkersPerAgent ?? DEFAULT_MAX_MARKERS_PER_AGENT;
  const readTurns = opts.readTurns ?? readRecentTurns;

  for (const codeName of codeNames) {
    try {
      const markerDir = slackPendingInboundDir(codeName);
      const markerFilenames = safeReaddir(markerDir).filter((f) => f.endsWith('.json'));
      if (markerFilenames.length === 0) {
        // No markers → no valid context; clear any leftover hints.
        pruneHintsExcept(codeName, new Set());
        continue;
      }

      const markers: StrandedMarkerRef[] = [];
      for (const filename of markerFilenames.slice(0, cap)) {
        const parsed = readStrandedMarker(join(markerDir, filename));
        if (parsed) markers.push({ filename, channel: parsed.channel, thread_ts: parsed.thread_ts });
      }
      if (markers.length === 0) {
        pruneHintsExcept(codeName, new Set());
        continue;
      }

      const transcriptDir = sessionTranscriptDir(getProjectDir(codeName));
      const allTurns = readTurns(transcriptDir, nowMs);
      const hints = allTurns.length > 0 ? computeRestartContextHints(markers, allTurns, nowMs) : [];

      const ctxDir = slackRestartContextDir(codeName);
      const freshFilenames = new Set<string>();
      for (const { filename, hint } of hints) {
        try {
          writeHintFile(join(ctxDir, filename), ctxDir, hint);
          freshFilenames.add(filename);
        } catch (err) {
          log(`[restart-context] ${codeName}: hint write failed for ${filename}: ${(err as Error).message}`);
        }
      }
      // Keep ONLY the hints freshly written this run: a live marker that yielded
      // no fresh hint must not keep a stale prior-boot file (would post outdated
      // context instead of the bare fallback). Also clears orphans + leftovers.
      pruneHintsExcept(codeName, freshFilenames);

      if (hints.length > 0) {
        log(`[restart-context] ${codeName}: wrote ${hints.length} topic hint(s) for stranded thread(s)`);
      }
    } catch (err) {
      log(`[restart-context] ${codeName}: failed (continuing): ${(err as Error).message}`);
    }
  }
}
